(ns app.front.view.audit-log.forms
  (:require [app.front.ui.section :as section]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :as futil]
            [app.front.view.audit-log.forms.subs :as subs]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]))

(defn value-text [tr
                  {:keys [format-date format-date-time] :as formatters}
                  {:keys [state text date time cells active? signer signed-at]}]
  (case state
    :value (cond
             date (format-date date)
             time (date-util/format-time time)
             cells (str/join "\n" (map (fn [{:keys [label value]}]
                                         (str label ": " (value-text tr formatters value)))
                                       cells))
             :else (or text (tr [:audit-log.forms/unavailable])))
    :signature (if active?
                 (let [signer (if (str/blank? signer)
                                (tr [:audit-log.forms/unknown-signer])
                                signer)]
                   (if signed-at
                     (tr [:audit-log.forms/signed-by-at] [signer (format-date-time signed-at)])
                     (tr [:audit-log.forms/signed-by] [signer])))
                 (tr [:audit-log.forms/inactive]))
    :unsigned (tr [:audit-log.forms/unsigned])
    :absent (tr [:audit-log.forms/absent])
    :protected (tr [:audit-log.forms/protected])
    (tr [:audit-log.forms/unavailable])))

(defn value-view [tr formatters {:keys [state cells personal-id] :as value} & [reveal-context]]
  (let [text (value-text tr formatters value)]
    (cond
      (and (= :protected state) personal-id (:can-reveal-personal-id? reveal-context))
      (let [{:keys [workspace-id form-id revealed-values]} reveal-context
            revealed                                       (get revealed-values personal-id)]
        (if (contains? revealed :value)
          [:div.whitespace-pre-wrap.break-words (:value revealed)]
          [:div.flex.items-center.gap-2
           [:span text]
           (cc/button {:kind     :ghost
                       :size     :small
                       :disabled (boolean (:loading? revealed))
                       :on-click #(>evt [::subs/reveal-personal-id workspace-id form-id personal-id])}
                      (tr [:form-field/personal-id-reveal]))]))

      (seq cells)
      (futil/for-frag [{:keys [label value]} cells]
        [:div.flex.flex-wrap.gap-x-1
         [:span (str label ":")]
         [value-view tr formatters value reveal-context]])

      (= :signature state)
      [:div.whitespace-pre-wrap.break-words text]

      (> (count text) 300)
      [:details
       [:summary.cursor-pointer (tr [:audit-log.forms/show-value])]
       [:div.whitespace-pre-wrap.break-words text]]

      :else
      [:div.whitespace-pre-wrap.break-words text])))

(defn transaction-rows [transactions]
  (->> transactions
       (mapcat (fn [{:keys [t timestamp actor changes]}]
                 (map-indexed (fn [index change]
                                (assoc change :row-id (str t "-" index) :timestamp timestamp :actor actor))
                              changes)))
       vec))

(defn- load-error [tr retry-event]
  [:div
   [:p {:role "alert"} (tr [:audit-log.forms/load-error])]
   (cc/button {:kind :tertiary :on-click #(>evt retry-event)} (tr [:audit-log.forms/retry]))])

(defn history-content [tr
                       {:keys [format-date-time] :as formatters}
                       {:keys [workspace-id
                               form-id
                               transactions
                               loading?
                               failed?
                               request-succeeded?
                               next-before-t]
                        :as   history}]
  [:div {:aria-busy (boolean loading?)}
   (when (seq transactions)
     [sortable-table/table
      {:tr      tr
       :items   (transaction-rows transactions)
       :id-func :row-id
       :cols    [[(tr [:audit-log.forms/field])
                  (fn [{:keys [label operation]}]
                    [:div.break-words
                     [:div.font-bold label]
                     [:div.text-sm (tr [(case operation
                                          :added :audit-log.forms/added
                                          :removed :audit-log.forms/removed
                                          :signed :audit-log.forms/signed
                                          :inactivated :audit-log.forms/inactivated
                                          :signed-again :audit-log.forms/signed-again
                                          :audit-log.forms/updated)])]])]
                 [(tr [:audit-log/old-attribute-value]) #(vector value-view tr formatters (:before %) history)]
                 [(tr [:audit-log/new-attribute-value]) #(vector value-view tr formatters (:after %) history)]
                 [(tr [:date/date]) (comp format-date-time :timestamp)]
                 [(tr [:user/user]) #(or (:actor %) (tr [:audit-log.forms/actor-unavailable]))]]}])
   (when loading?
     [:div {:role "status"}
      (tr [:audit-log.forms/loading])
      (when (empty? transactions)
        [section/section-skeleton])])
   (when (and request-succeeded? (not loading?) (empty? transactions))
     [:p (tr [:audit-log.forms/empty])])
   (when failed?
     [load-error tr [::subs/fetch-form-history workspace-id form-id]])
   (when (and next-before-t (not failed?))
     (cc/button {:kind     :tertiary
                 :disabled loading?
                 :on-click #(>evt [::subs/fetch-form-history workspace-id form-id])}
                (tr [:audit-log.forms/load-more])))])

(defn form-groups [tr
                   formatters
                   {:keys [workspace-id
                           forms
                           pages
                           operation-id
                           loading?
                           failed?
                           request-succeeded?]}]
  [:div {:aria-busy (boolean loading?)}
   (cond
     loading?
     [:div {:role "status"} (tr [:audit-log.forms/loading]) [section/section-skeleton]]

     failed?
     [load-error tr [::subs/fetch-forms workspace-id]]

     (and request-succeeded? (empty? forms))
     [:p (tr [:audit-log.forms/no-forms])])
   (futil/for-frag [{:keys [form-id name phase sign-off? authority-form?] :as form} forms]
     [section/section (str "form-history-" operation-id "-" form-id)
      {:tr                   tr
       :class                (str "compact [&>.section-heading>div:first-child]:min-w-0 "
                                  "[&>.section-heading>div:first-child>div]:min-w-0")
       :initially-collapsed? true
       :on-expand            #(>evt [::subs/expand workspace-id form-id])
       :heading-component    (fn [_ _]
                               [:h3.mb-0.break-words.whitespace-normal
                                [:span.inline-flex.flex-wrap.items-center.gap-2
                                 name
                                 (when sign-off?
                                   (cc/tag {:type "gray" :size "sm"} (tr [:audit-log.forms/sign-off])))
                                 (when authority-form?
                                   (cc/tag {:type "gray" :size "sm"} (tr [:audit-log.forms/authority])))]
                                (when phase
                                  [:span.block.text-sm.font-normal phase])])}
      nil
      (when-let [page (get pages form-id)]
        [history-content tr formatters (merge page form {:workspace-id workspace-id})])])])

(defn view []
  (let [tr           (<sub [::ws-subs/tr])
        workspace-id (<sub [::ws-subs/workspace-id])
        history      (<sub [::subs/history])]
    [section/section "form-history-section" {:tr tr} :audit-log.forms/title
     (when (= workspace-id (:workspace-id history))
       [form-groups tr {:format-date-time (<sub [::date-util/date-time-fn])
                        :format-date      (<sub [::date-util/date-fn])}
        history])]))
